import { createTracedAsyncThunk } from '../../../tracing/trace';
import { Api } from '../../Api/Api';
import { AppDispatch, RootState } from '../Store';

export interface IJdConnectInvitationResponse
{
	SellerId: string;
    GrowerId: string;
    Token: string;
    CreatedDate: string;
    Status: string;
}

export interface IJdConnectInvitationRequest
{
	GrowerId: string;
	SetLink: (link: string) => void;
	SetQrCode: (qrCode: string) => void;
	SetErrorMessage: (message: string) => void;
	StartLoading: () => void;
	EndLoading: () => void; 
}

export const createInvitationAndGetQrCodeWithLink = createTracedAsyncThunk<IJdConnectInvitationResponse, IJdConnectInvitationRequest, { dispatch: AppDispatch, state: RootState }>(
	'jdconnect/createinvitation',
	async (context, request: IJdConnectInvitationRequest, thunkAPI) => 
	{
		try
		{
			request.StartLoading();

			const { GrowerId } = request;
			const api = new Api('/api/6', thunkAPI.getState().auth.userAuthToken, context);
			
			const response = await api.postAsync<IJdConnectInvitationResponse>(`growers/${GrowerId}/jd-connect/invitations`, '');

			if (response.ErrorCode === null && response.Success)
			{
				const qrCodeWithLinkResponse = await thunkAPI.dispatch(getQrCodeWithLink({token: response.Data.Token, growerId: GrowerId}));

				if (qrCodeWithLinkResponse.meta.requestStatus === 'fulfilled')
				{
					const qrCodeResponse = qrCodeWithLinkResponse.payload as IJdConnectQrResponse;
					request.SetLink(qrCodeResponse.Url);
					request.SetQrCode(qrCodeResponse.QrCode);
				}
				else
				{
					request.SetErrorMessage('There was a problem creating the invitation. Please try again later.');
				}

				return response.Data;
			}
			else
			{
				request.SetErrorMessage('There was a problem creating the invitation. Please try again later.');
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			request.SetErrorMessage('There was a problem creating the invitation. Please try again later.');
			return thunkAPI.rejectWithValue(e.message);
		}
		finally
		{
			request.EndLoading();
		}
	}
);

export interface IJdConnectQrResponse
{
	QrCode: string;
	Url: string;
	GrowerId: string;
}

export const getQrCodeWithLink = createTracedAsyncThunk<IJdConnectQrResponse, {token: string, growerId: string}, { dispatch: AppDispatch, state: RootState }>(
	'jdconnect/getqrcodeandlink',
	async (context, request: {token: string, growerId: string}, thunkAPI) => 
	{
		try
		{
			const { token, growerId } = request;
			const api = new Api('/api/6', thunkAPI.getState().auth.userAuthToken, context);
			
			const response = await api.getAsync<IJdConnectQrResponse>(`jd-connect/invitations/${token}/qrwithurl`);

			if (response.ErrorCode === null && response.Success)
			{
				// Attach the grower Id from the request
				const data: IJdConnectQrResponse =
				{
					QrCode: response.Data.QrCode,
					Url: response.Data.Url,
					GrowerId: growerId,
				};

				return data;
			}
			else
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

export const deleteGrowerJdConnection = createTracedAsyncThunk<boolean, {growerId: string}, { dispatch: AppDispatch, state: RootState }>(
	'jdconnect/removegrowerconnection',
	async (context, request: {growerId: string}, thunkAPI) => 
	{
		try
		{
			const { growerId } = request;
			const api = new Api('/api/6', thunkAPI.getState().auth.userAuthToken, context);
			
			const response = await api.deleteAsync<boolean>(`growers/${growerId}/jd-connect/delete`);

			if (response.ErrorCode === null && response.Success)
			{
				return response.Data;
			}
			else
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);